import WidgetBoard from '../../../../components/WidgetBoard';
import { usePublicIntegration } from '../../../../context/PublicIntegrationContext';
import { externalLinkProps, isExternalServiceHref } from '@frontend/utils/externalLink';
import { resolveServicePath } from '@frontend/config/serviceUrlRegistry';
import type { EcosystemDefinition, EcosystemProject } from '@frontend/types';
import type { EcosystemServiceOverview } from '@frontend/pages/dashboard/types';

interface ProjectDetailProps {
  selectedProject: EcosystemProject;
  ecosystemId: string;
  ecosystemDefs: EcosystemDefinition[];
  overviewForEco: EcosystemServiceOverview | null;
}

export default function ProjectDetail({ selectedProject, ecosystemId, ecosystemDefs, overviewForEco }: ProjectDetailProps) {
  const builds = overviewForEco?.woodpecker?.recentBuilds || [];
  const commits = overviewForEco?.gitea?.recentCommits || [];
  const integration = usePublicIntegration();
  const nextcloudHref =
    selectedProject.nextcloudUrl ||
    (integration.nextcloudUserOidcLoginPath.startsWith('/')
      ? integration.nextcloudUserOidcLoginPath
      : `/${integration.nextcloudUserOidcLoginPath}`);

  const serviceLinks = [
    {
      href: selectedProject.openprojectUrl || resolveServicePath('openproject', 'sso_entry'),
      title: 'OpenProject',
      subtitle: 'Planning, stories, and sprint tracking',
    },
    {
      href: selectedProject.giteaUrl || resolveServicePath('gitea', 'sso_entry'),
      title: 'Gitea Repository',
      subtitle: 'Code, pull requests, and issue workflow',
    },
    {
      href: nextcloudHref,
      title: 'Nextcloud Files',
      subtitle: 'Project documents, specs, and uploads',
    },
  ];

  return (
    <div className="tree-dashboard-panel">
      <h2>{selectedProject.name}</h2>
      <p className="tree-dashboard-muted">{selectedProject.description || 'No project description yet.'}</p>

      <div className="tree-dashboard-link-grid">
        {serviceLinks.map((link) => (
          <a
            key={link.title}
            className="tree-dashboard-link-card"
            href={link.href}
            {...(isExternalServiceHref(link.href) ? externalLinkProps : {})}
          >
            <div className="tree-dashboard-link-title">{link.title}</div>
            <div className="tree-dashboard-link-subtitle">{link.subtitle}</div>
          </a>
        ))}
      </div>

      <div className="tree-dashboard-split">
        <div>
          <h3 className="tree-dashboard-section-title">Recent Build Activity</h3>
          {builds.length === 0 ? (
            <p className="tree-dashboard-muted">No recent CI pipeline entries.</p>
          ) : (
            <ul className="tree-dashboard-activity-list">
              {builds.slice(0, 5).map((build) => (
                <li key={`${build.number}:${build.branch}`}>
                  #{build.number} - {build.status || 'unknown'} ({build.branch || 'n/a'})
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="tree-dashboard-section-title">Recent Commits</h3>
          {commits.length === 0 ? (
            <p className="tree-dashboard-muted">No recent commits available.</p>
          ) : (
            <ul className="tree-dashboard-activity-list">
              {commits.slice(0, 5).map((commit) => (
                <li key={commit.sha}>{(commit.message || '').split('\n')[0]}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <WidgetBoard
        contextType="project"
        contextId={selectedProject.id}
        ecosystemId={ecosystemId}
        ecosystemServices={ecosystemDefs.find((e) => e.id === ecosystemId)?.services}
      />
    </div>
  );
}
